import React, { useLayoutEffect, useRef } from "react";
import { renderStatusIndicator } from "@/utils/mediautils/RenderStatusIndicator";
import { getHostLogoStyleByHostId } from "@/utils";
import { truncateText } from "@/utils/utilFunctions/truncateText";

interface DenmarkSelectedArticlesChipsProps {
  selectedArticle: any[];
  selectedChip?: number;
  hosts: any[];
  hostOptions: any[];
  denmarkHosts: any[];
  savedSocials?: any[];
  onSelectChip?: (sectionId: number) => void;
  onRemoveChip: (item: any, index: number) => void;
}

const DenmarkSelectedArticlesChips: React.FC<
  DenmarkSelectedArticlesChipsProps
> = ({
  selectedArticle,
  selectedChip,
  hosts,
  hostOptions,
  denmarkHosts,
  savedSocials,
  onSelectChip,
  onRemoveChip,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const chipElsRef = useRef<Map<number, HTMLDivElement>>(new Map());

  useLayoutEffect(() => {
    if (selectedChip == null) return;
    const el = chipElsRef.current.get(Number(selectedChip));
    const container = scrollRef.current;
    if (!el || !container) return;
    if (container.scrollWidth <= container.clientWidth) return;

    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const pad = 4;
    const fullyVisible =
      eRect.left >= cRect.left + pad && eRect.right <= cRect.right - pad;

    if (fullyVisible) return;

    el.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [selectedChip, selectedArticle]);

  if (!selectedArticle || selectedArticle.length === 0) return null;

  return (
    <div
      className="mt-2 mb-3"
      ref={scrollRef}
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "scroll",
        overflowY: "hidden",
        paddingBottom: "4px",
        scrollbarGutter: "stable",
        WebkitOverflowScrolling: "touch",
      }}
      onWheel={(event) => {
        const container = scrollRef.current;
        if (!container) return;
        const hasHorizontalOverflow =
          container.scrollWidth > container.clientWidth;
        if (!hasHorizontalOverflow) return;
        if (event.deltaY === 0 && event.deltaX === 0) return;
        event.preventDefault();
        container.scrollLeft +=
          event.deltaY !== 0 ? event.deltaY : event.deltaX;
      }}
    >
      <div
        className="d-flex gap-2"
        style={{ width: "max-content", minWidth: "100%", flexWrap: "nowrap" }}
      >
        {selectedArticle.map((item: any, index: number) => {
          const hostId = item.host;
          const logoStyle = getHostLogoStyleByHostId(
            hostId,
            hosts,
            hostOptions,
            denmarkHosts
          );
          const isSelectedChip =
            selectedChip != null &&
            Number(item?.section) === Number(selectedChip);
          const savedSocial =
            savedSocials != null
              ? savedSocials.find(
                  (s: any) =>
                    s?.job_type === "denmark" &&
                    Number(s?.section) === Number(item?.section)
                )
              : null;
          const isInSavedSocials = savedSocial != null;
          const hideRemove = isInSavedSocials;
          const savedSchedule = savedSocial?.schedule;
          const hasSavedSchedule =
            savedSchedule != null &&
            Number(savedSchedule) > 0 &&
            Number(savedSchedule) > Date.now();
          const futureOnItem =
            item?.schedule != null && Number(item.schedule) > Date.now();
          const effectiveSchedule = isInSavedSocials
            ? hasSavedSchedule ||
              (typeof item?.status === "number" &&
                item.status === 10 &&
                futureOnItem)
            : !!(item?.schedule && item.schedule > Date.now());

          const tooltipTs =
            futureOnItem && item?.schedule != null
              ? Number(item.schedule)
              : hasSavedSchedule && savedSchedule != null
                ? Number(savedSchedule)
                : null;
          const scheduleTooltip =
            tooltipTs != null
              ? `Scheduled On: ${new Date(tooltipTs).toLocaleString()}`
              : "Scheduled";

          const showStatusIndicator =
            typeof item?.status === "number" || effectiveSchedule;

          return (
            <div
              key={`edit-chip-${item.section ?? index}`}
              ref={(node) => {
                const id = Number(item?.section);
                if (Number.isNaN(id)) return;
                if (node) chipElsRef.current.set(id, node);
                else chipElsRef.current.delete(id);
              }}
              className="social-chip gap-1 p-1 position-relative flex-shrink-0"
              style={{
                cursor: "pointer",
                minWidth: "100px",
                maxWidth: "150px",
                border: isSelectedChip ? "1px solid #50c377" : undefined,
                backgroundColor: isSelectedChip ? "#e8f8ed" : undefined,
                padding: "3px 6px",
              }}
              onClick={() => {
                if (onSelectChip) {
                  onSelectChip(Number(item.section));
                }
              }}
            >
              <div
                className="d-flex align-items-center gap-1 min-w-0 flex-grow-1"
                style={{ minWidth: 0 }}
              >
                {logoStyle && Object.keys(logoStyle).length > 0 ? (
                  <div
                    style={{
                      ...logoStyle,
                      flexShrink: 0,
                      minWidth: "24px",
                      minHeight: "24px",
                      overflow: "visible",
                    }}
                    aria-hidden
                  />
                ) : (
                  <i
                    className="bi bi-file-text flex-shrink-0 text-secondary"
                    style={{ fontSize: "14px" }}
                    aria-hidden
                  />
                )}
                <span
                  className=" d-block"
                  style={{
                    fontSize: "12px",
                    lineHeight: "16px",
                    minWidth: 0,
                  }}
                  title={
                    typeof item.channel_name === "string"
                      ? item.channel_name
                      : undefined
                  }
                >
                  {truncateText(item.channel_name, 13)}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                {showStatusIndicator &&
                  (effectiveSchedule ? (
                    <i
                      className="bi bi-clock text-secondary flex-shrink-0"
                      style={{ fontSize: "12px" }}
                      title={scheduleTooltip}
                    />
                  ) : (
                    renderStatusIndicator(
                      typeof item?.status === "number" ? item.status : 10
                    )
                  ))}
                {item?.status !== 10 && !hideRemove && (
                  <i
                    className="bi bi-x"
                    style={{ cursor: "pointer", paddingTop: "2px" }}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveChip(item, index);
                    }}
                    aria-label="Remove section"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DenmarkSelectedArticlesChips;
